import {hasServerErrorCode} from 'app/services/server-error-code';
import {growthReasonKey} from 'app/content-page/common/grade-growth/grade-growth-presentation';
import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {
  DealPayrollGradeCode, PAYROLL_CALCULATION_METHOD, PayrollCalculationMethod,
  UserPayrollSettingsRequest, UserPayrollSettingsResponse
} from 'app/models/deal-payroll.model';
import {DealPayrollSettingsService} from 'app/services/deal-payroll-settings.service';
import {
  DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY, DEAL_PAYROLL_USER_SETTINGS_HTTP_STATUS,
  DEAL_PAYROLL_USER_SETTINGS_INITIAL_SEQUENCE, DEAL_PAYROLL_USER_REVISION_CONFLICT
} from './deal-payroll-user-settings.constants';
import {DealPayrollUserSettingsState} from './deal-payroll-user-settings.state';

const MIN_USER_REVISION = 0;
const GROWTH_ERROR_PREFIX = 'GROWTH_';
const STRING_TYPE = 'string';

@Injectable()
export class DealPayrollUserSettingsFacade implements OnDestroy {
  readonly state = new DealPayrollUserSettingsState();
  readonly settingsSaved = new EventEmitter<UserPayrollSettingsResponse>();

  private currentUserId: string = null;
  private requestSequence = DEAL_PAYROLL_USER_SETTINGS_INITIAL_SEQUENCE;
  private saveSequence = DEAL_PAYROLL_USER_SETTINGS_INITIAL_SEQUENCE;
  private destroy$ = new Subject<void>();
  private destroyed = false;
  private reviewedGradeCode: DealPayrollGradeCode = null;

  constructor(private settingsService: DealPayrollSettingsService) {}

  ngOnDestroy(): void {
    this.destroyed = true;
    this.state.resetForUserChange();
    this.currentUserId = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeUser(userId: string): void {
    if (this.destroyed) { return; }
    this.currentUserId = userId;
    this.reviewedGradeCode = null;
    this.requestSequence++;
    this.saveSequence++;
    this.state.resetForUserChange();
    this.reload(userId);
  }

  reload(userId: string, preserveError: boolean = false): void {
    if (!userId || this.destroyed || this.state.saving || this.state.loading) {
      return;
    }
    this.currentUserId = userId;
    const sequence = ++this.requestSequence;
    this.state.startLoading(preserveError);
    this.settingsService.getPayrollCalculationSettings(userId)
      .takeUntil(this.destroy$).subscribe(
        response => this.applyResponse(response, userId, sequence),
        error => this.handleLoadError(error, userId, sequence)
      );
  }

  selectPayrollMethod(method: PayrollCalculationMethod): void {
    if (!this.canSelectMethod() || this.state.settings.payrollCalculationMethod === method) { return; }
    if (method === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL && !this.state.canEnableDeal) { return; }
    this.reviewedGradeCode = null;
    this.state.beginMethodSelection(method);
    this.beginSave({payrollCalculationMethod: method, confirmGradeAssignment: false});
  }

  confirmGradeSelection(): void {
    const gradeCode = this.reviewedGradeCode;
    if (!this.canEditGrade(gradeCode) || !this.state.canStartDealAssignment) { return; }
    this.reviewedGradeCode = null;
    const settings = this.state.settings;
    this.beginSave({payrollCalculationMethod: settings.payrollCalculationMethod, confirmGradeAssignment: true,
      gradeCode: gradeCode, assignmentRevision: settings.assignmentRevision});
  }

  reviewAssignment(showWarning: () => void): void {
    if (!this.canEditGrade(this.state.selectedGradeCode)) { this.state.showValidation(); return; }
    const userId = this.currentUserId;
    const sequence = ++this.requestSequence;
    const context = {userId: userId, sequence: sequence, gradeCode: this.state.selectedGradeCode};
    this.reviewedGradeCode = null;
    this.state.startLoading(false);
    this.settingsService.getPayrollCalculationSettings(userId).takeUntil(this.destroy$).subscribe(
      response => this.finishAssignmentReview(response, context, showWarning),
      error => this.handleLoadError(error, userId, sequence));
  }

  private finishAssignmentReview(response: UserPayrollSettingsResponse,
                                 context: {userId: string; sequence: number; gradeCode: DealPayrollGradeCode},
                                 showWarning: () => void): void {
    if (!this.acceptLoadResponse(response, context.userId, context.sequence)) { return; }
    this.state.useSettings(response);
    if (!this.canEditGrade(context.gradeCode)) { return; }
    this.state.selectGrade(context.gradeCode);
    this.reviewedGradeCode = context.gradeCode;
    showWarning();
  }

  selectPosition(positionKey: string): void {
    if (!this.canEditDealAssignment() || this.state.selectedPositionKey === positionKey) {
      return;
    }
    this.state.selectPosition(positionKey);
  }

  selectGrade(gradeCode: DealPayrollGradeCode): void {
    if (!this.canSelectMethod()) { return; }
    if (gradeCode !== null && !this.canEditGrade(gradeCode)) { return; }
    this.state.selectGrade(gradeCode);
    this.reviewedGradeCode = null;
  }

  resume(): void {
    if (!this.state.canResume || !this.validRevision(this.state.settings)) { return; }
    const userId = this.currentUserId;
    const settings = this.state.settings;
    const request = {expectedUserRevision: settings.userRevision, operationToken: settings.pendingOperationToken};
    const sequence = ++this.saveSequence;
    this.state.startSaving(settings.payrollCalculationMethod);
    this.settingsService.resumePayrollCalculationSettings(userId, request).takeUntil(this.destroy$).subscribe(
      response => this.applySavedResponse(response, userId, sequence),
      error => this.handleSaveError(error, userId, sequence));
  }

  private validRevision(response: UserPayrollSettingsResponse): boolean {
    return !!response && Number.isSafeInteger(response.userRevision)
      && response.userRevision >= MIN_USER_REVISION;
  }

  private canSelectMethod(): boolean {
    return this.state.canManageSettings && !this.state.loading && !this.state.saving;
  }

  private canEditDealAssignment(): boolean {
    return this.state.canManageSettings && this.state.dealSelected
      && !this.state.loading && !this.state.saving;
  }

  private canEditGrade(gradeCode: DealPayrollGradeCode): boolean {
    return this.canSelectMethod()
      && this.state.settings.grades.some(grade => grade.enabled && grade.gradeCode === gradeCode);
  }

  private beginSave(request: UserPayrollSettingsRequest): void {
    if (!this.state.canManageSettings || !this.validRevision(this.state.settings)) { return; }
    this.sendSave(Object.assign({}, request, {expectedUserRevision: this.state.settings.userRevision}));
  }

  private sendSave(request: UserPayrollSettingsRequest): void {
    this.state.startSaving(request.payrollCalculationMethod);
    const userId = this.currentUserId;
    const sequence = ++this.saveSequence;
    this.settingsService.updatePayrollCalculationSettings(userId, request)
      .takeUntil(this.destroy$).subscribe(
        response => this.applySavedResponse(response, userId, sequence),
        error => this.handleSaveError(error, userId, sequence)
      );
  }

  private applyResponse(response: UserPayrollSettingsResponse, userId: string, sequence: number): void {
    if (!this.acceptLoadResponse(response, userId, sequence)) { return; }
    this.state.clearError();
    this.state.useSettings(response);
  }

  private applySavedResponse(
    response: UserPayrollSettingsResponse,
    userId: string,
    sequence: number
  ): void {
    if (!this.isCurrentSave(userId, sequence)) {
      return;
    }
    if (!response || response.userId !== userId || !this.validRevision(response)) {
      this.rejectSavedResponse();
      return;
    }
    this.state.finishSaved(response);
    if (!response.pendingOperationToken) { this.settingsSaved.emit(response); }
  }

  private rejectSavedResponse(): void {
    this.state.rejectSavedResponse();
  }

  private handleSaveError(error: HttpErrorResponse, userId: string, sequence: number): void {
    if (!this.isCurrentSave(userId, sequence)) {
      return;
    }
    this.state.prepareSaveError(this.blockerCodes(error));
    if (this.isUserRevisionConflict(error)) {
      this.state.rejectUserRevisionConflict();
      return;
    }
    this.finishSaveError(error);
  }

  private isUserRevisionConflict(error: HttpErrorResponse): boolean {
    return !!error && error.status === DEAL_PAYROLL_USER_SETTINGS_HTTP_STATUS.CONFLICT
      && hasServerErrorCode(error, DEAL_PAYROLL_USER_REVISION_CONFLICT);
  }

  private finishSaveError(error: HttpErrorResponse): void {
    const forbidden = !!error
      && error.status === DEAL_PAYROLL_USER_SETTINGS_HTTP_STATUS.FORBIDDEN;
    this.state.finishSaveError(this.saveErrorKey(error), forbidden);
  }

  private saveErrorKey(error: HttpErrorResponse): string {
    const code = error && error.error && error.error.code;
    if (typeof code === STRING_TYPE && code.indexOf(GROWTH_ERROR_PREFIX) === MIN_USER_REVISION) {
      return growthReasonKey(code);
    }
    if (error && error.status === DEAL_PAYROLL_USER_SETTINGS_HTTP_STATUS.CONFLICT) {
      return DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.STALE;
    }
    return error && error.status === DEAL_PAYROLL_USER_SETTINGS_HTTP_STATUS.FORBIDDEN
      ? DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.FORBIDDEN
      : DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.SAVE;
  }

  private handleLoadError(error: HttpErrorResponse, userId: string, sequence: number): void {
    if (!this.isCurrentRequest(userId, sequence)) {
      return;
    }
    const errorKey = error && error.status === DEAL_PAYROLL_USER_SETTINGS_HTTP_STATUS.FORBIDDEN
      ? DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.FORBIDDEN
      : DEAL_PAYROLL_USER_SETTINGS_ERROR_KEY.LOAD;
    this.state.rejectLoad(errorKey, this.blockerCodes(error));
  }

  private acceptLoadResponse(
    response: UserPayrollSettingsResponse,
    userId: string,
    sequence: number
  ): boolean {
    if (!this.isCurrentRequest(userId, sequence)) {
      return false;
    }
    if (response && response.userId === userId && this.validRevision(response)) {
      return true;
    }
    this.state.rejectInvalidLoad();
    return false;
  }

  private blockerCodes(error: HttpErrorResponse): string[] {
    return error && error.error && error.error.blockers ? error.error.blockers : [];
  }

  private isCurrentRequest(userId: string, sequence: number): boolean {
    return this.currentUserId === userId && this.requestSequence === sequence;
  }

  private isCurrentSave(userId: string, sequence: number): boolean {
    return this.currentUserId === userId && this.saveSequence === sequence;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/deal-payroll-user-settings/deal-payroll-user-settings.facade.ts